import { splitName } from './split-name'

/**
 * Get initials from a full name string for avatar display.
 *
 * Works for both user names (e.g. "John Doe") and organization names (e.g. "Acme Corporation").
 * Returns at most 2 characters: the first letter of the first name part and, when present,
 * the first letter of the last name part. Single-word names yield a single initial.
 *
 * @param fullName - The full name string (user or organization name)
 * @param options - Optional settings
 * @param options.maxLength - Maximum number of initials to return (default: 2)
 * @param options.separator - Separator used to split the name (default: ' ')
 * @param options.lastNamePosition - How to interpret "last" name when splitting; see {@link splitName}
 *
 * @returns Uppercase initials, or empty string if the name is empty after normalization
 *
 * @example
 * getInitials('John Doe') // 'JD'
 * getInitials('John') // 'J'
 * getInitials('John Michael Doe') // 'JD'
 * getInitials('Acme Corporation') // 'AC'
 * getInitials('  Jane   Smith  ') // 'JS'
 * getInitials('') // ''
 */
export function getInitials(
  fullName: string,
  options: {
    maxLength?: number
    separator?: string
    lastNamePosition?: 'last' | 'first' | 'none'
  } = {},
): string {
  const { maxLength = 2, separator, lastNamePosition } = options

  const { firstName, lastName } = splitName(fullName, {
    separator,
    lastNamePosition: lastNamePosition ?? 'last',
    normalize: true,
  })

  const parts: string[] = []
  if (firstName.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    parts.push(firstName[0]!)
  }
  if (lastName && lastName.length > 0 && parts.length < maxLength) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    parts.push(lastName[0]!)
  }

  return parts.join('').toUpperCase().slice(0, maxLength)
}
